import React from 'react';
import { SvgIconProps, Tooltip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';

import type { BaseStepType, IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { getStepConfig } from '../getStepConfig';
import { MHRIcon } from './MHRIcon.tsx';

const throwNeverStatus = (status: never) => {
  throw new Error(`Status does not exist: ${status}`);
};

interface IconWrapperProps extends React.ComponentProps<typeof Tooltip> {
  children: React.ReactElement<SvgIconProps>;
}

const IconWrapper = ({ title, children, ...props }: IconWrapperProps) => (
  <Tooltip title={title} placement="top" {...props}>
    {children}
  </Tooltip>
);

interface BaseStepIconProps<TBaseStepType extends BaseStepType> extends SvgIconProps {
  step: IntakeStatusBaseSteps[TBaseStepType];
  noTooltip?: boolean;
}

export const BaseStepIcon = <TBaseStepType extends BaseStepType>({
  step,
  noTooltip,
  ...iconProps
}: BaseStepIconProps<TBaseStepType>) => {
  const stepConfig = getStepConfig(step.id);
  const { label } = stepConfig.statuses[step.currentStatus.status];

  const type = step.id;

  const tooltipTitle = noTooltip ? undefined : label;

  switch (type) {
    case 'ID_VERIFICATION':
      return (
        <IconWrapper title={tooltipTitle}>
          <VerifiedUserIcon {...iconProps} />
        </IconWrapper>
      );
    case 'MEDICARE_VERIFICATION':
      return (
        <IconWrapper title={tooltipTitle}>
          <CreditScoreIcon {...iconProps} />
        </IconWrapper>
      );
    case 'UPLOAD_HEALTHCARE_SUMMARY':
      return (
        <IconWrapper title={tooltipTitle}>
          <UploadFileIcon {...iconProps} />
        </IconWrapper>
      );
    case 'UPLOAD_REFERRAL_LETTER_OF_SUPPORT':
      return (
        <IconWrapper title={tooltipTitle}>
          <AssignmentRoundedIcon {...iconProps} />
        </IconWrapper>
      );
    case 'GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY':
      return (
        <IconWrapper title={tooltipTitle}>
          <EditDocumentIcon {...iconProps} />
        </IconWrapper>
      );
    case 'MHR_ACCESS':
      return (
        <IconWrapper title={tooltipTitle}>
          <MHRIcon {...iconProps} />
        </IconWrapper>
      );
    case 'ACCOUNT_VERIFICATION':
      return (
        <IconWrapper title={tooltipTitle}>
          <ManageAccountsIcon {...iconProps} />
        </IconWrapper>
      );
    default:
      return throwNeverStatus(type);
  }
};
